import { RoleLink } from "@/components/client-role";
import { type Health, OpeningsBadge, STATUS_RANK, StatusBadge, roleHealth } from "@/components/dashboard/role-status";
import { cn } from "@/lib/utils";
import type { PipelineCount, Tier, TierRow } from "@ow/shared";
import { ArrowUpDown, Check, Maximize2, Minus, Network, Plus, Rows3, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// The home screen: ONE roles table across every tier (tiers are a filter, not navigation), for a
// client who is a pure OBSERVER of recruitment progress — no actions to take, just "where does each
// search stand and is it on track". A row hover reveals the full stage distribution; a toggle re-cuts
// the same roles as a top-down org chart (root → seniority levels), the way an org chart actually reads.

type Row = TierRow & { tier: Tier };
type ViewMode = "table" | "org";

const TIER_ORDER: Tier[] = ["csuite", "vp_director", "general"];
const TIER_LABEL: Record<Tier, string> = { csuite: "C-Suite", vp_director: "VP / Director", general: "General" };

// The role health read + status pill + headcount treatment are shared with role detail (role-status.tsx)
// so a role looks identical wherever it appears.
const health = (r: Row): Health => roleHealth(r);

// Sort options for the roles table. Default keeps the tier grouping; the rest surface what a senior
// observer scans for — how close to closing, how much activity, health, or plain alphabetical.
type SortKey = "default" | "near" | "applicants" | "status" | "az";
const SORT_OPTS: { v: SortKey; label: string }[] = [
  { v: "default", label: "Tier order" },
  { v: "near", label: "Near closure" },
  { v: "applicants", label: "Candidates" },
  { v: "status", label: "Status" },
  { v: "az", label: "Role A–Z" },
];

// One column template shared by the header and every row so the table reads straight down. Every cell
// is a single value on a single line; every column — text and number alike — is left-aligned, so the
// eye follows one consistent edge down the whole table.
const GRID =
  "grid grid-cols-[minmax(0,2.2fr)_104px_minmax(0,1.4fr)_88px_112px_136px_minmax(0,1.4fr)] items-center gap-6";

export function RolesHome({ tiers }: { tiers: Record<Tier, TierRow[]> }) {
  const [view, setView] = useState<ViewMode>("table");
  const [tier, setTier] = useState<Tier | "all">("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("default");

  const rows: Row[] = useMemo(() => TIER_ORDER.flatMap((t) => tiers[t].map((r) => ({ ...r, tier: t }))), [tiers]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (tier !== "all" && r.tier !== tier) return false;
      if (q && !`${r.title} ${r.function ?? ""} ${r.ownerName ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const arr = [...filtered];
    switch (sort) {
      case "near":
        arr.sort((a, b) => b.nearClosure - a.nearClosure);
        break;
      case "applicants":
        arr.sort((a, b) => b.activeCandidates - a.activeCandidates);
        break;
      case "status":
        arr.sort((a, b) => STATUS_RANK[health(a).kind] - STATUS_RANK[health(b).kind]);
        break;
      case "az":
        arr.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break; // keep the tier grouping order
    }
    return arr;
  }, [rows, tier, query, sort]);

  // Tier chips + search stay live in BOTH views. In the table they filter rows; in the org chart they
  // don't remove nodes (that would break the branching) — instead they dim the non-matching cards and
  // the search zooms the canvas to the first match. Sort is the only control that has no meaning for a
  // tree, so it's the one thing disabled in org view.
  const orgView = view === "org";
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1">
            <TierChip active={tier === "all"} onClick={() => setTier("all")}>
              All roles
            </TierChip>
            {TIER_ORDER.map((t) => (
              <TierChip key={t} active={tier === t} onClick={() => setTier(t)}>
                {TIER_LABEL[t]}
              </TierChip>
            ))}
          </div>
          <span className="font-mono text-xs uppercase tracking-[0.04em] text-muted-foreground">
            <span className="font-medium tabular-nums text-foreground">{visible.length}</span>{" "}
            {visible.length === 1 ? "role" : "roles"}
          </span>
          <div className="relative ml-auto min-w-[180px] max-w-xs flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={orgView ? "Search to highlight in the chart…" : "Search roles, functions, leads…"}
              aria-label="Search roles"
              className="h-9 w-full rounded-md border bg-card pl-9 pr-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
          <SortMenu value={sort} onChange={setSort} disabled={orgView} />
        </div>
        <span className="hidden h-6 w-px shrink-0 bg-border sm:block" />
        <ViewToggle value={view} onChange={setView} />
      </div>

      {view === "org" ? (
        <RolesOrgChart rows={rows} tier={tier} query={query} />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
          No roles match.
        </div>
      ) : (
        <RolesTable rows={visible} />
      )}
    </section>
  );
}

/** Compact sort control for the table — filters left, this + search grouped on the right (per data-
 *  table toolbar convention). Shows the active sort so the current order is never a mystery. */
function SortMenu({
  value,
  onChange,
  disabled,
}: { value: SortKey; onChange: (v: SortKey) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTS.find((o) => o.v === value);
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-card px-2.5 text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
      >
        <ArrowUpDown className="size-3.5" />
        <span className="hidden sm:inline">{value === "default" ? "Sort" : current?.label}</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close sort menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Sort by</p>
            {SORT_OPTS.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent"
              >
                {o.label}
                {value === o.v ? <Check className="size-3.5 text-foreground" /> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function RolesTable({ rows }: { rows: Row[] }) {
  return (
    <div className="rounded-xl border bg-card">
      <div className={cn(GRID, "border-b bg-muted/30 px-5 py-3")}>
        <span className="eyebrow">Role</span>
        <span className="eyebrow">Tier</span>
        <span className="eyebrow">Function</span>
        <span className="eyebrow">Candidates</span>
        <span className="eyebrow">Near closure</span>
        <span className="eyebrow">Status</span>
        <span className="eyebrow">Lead</span>
      </div>
      <div className="divide-y">
        {rows.map((r) => (
          <RoleRow key={r.id} r={r} />
        ))}
      </div>
    </div>
  );
}

function RoleRow({ r }: { r: Row }) {
  const h = health(r);
  return (
    <div className="group relative">
      <RoleLink
        to="/search/$searchId"
        params={{ searchId: r.id }}
        className={cn(
          GRID,
          "px-5 py-3.5 outline-none transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{r.title}</span>
          <OpeningsBadge openings={r.openings} />
        </div>
        <span className="truncate text-sm text-muted-foreground">{TIER_LABEL[r.tier]}</span>
        <span className="truncate text-sm text-muted-foreground">{r.function ?? "—"}</span>
        <span className="text-sm tabular-nums text-foreground">{r.activeCandidates}</span>
        <span
          className={cn("text-sm tabular-nums", r.nearClosure > 0 ? "text-foreground" : "text-muted-foreground/50")}
        >
          {r.nearClosure}
        </span>
        <span>
          <StatusBadge h={h} />
        </span>
        <span className="truncate text-sm text-muted-foreground">{r.ownerName ?? "—"}</span>
      </RoleLink>
      <StageDistribution stages={r.stages} />
    </div>
  );
}

/** Hover popover: the honest per-stage distribution behind a role's headline numbers — so "many
 *  candidates at many stages" is visible on demand without a misleading single-stage column. Anchored
 *  to drop consistently from just below the role name (left-aligned to the row) with a small caret, so
 *  it always appears in the same predictable place and reads as "details for THIS role". Fades in on
 *  hover rather than snapping, and is pointer-transparent so it never traps the cursor. */
function StageDistribution({ stages }: { stages: PipelineCount[] }) {
  const total = stages.reduce((s, x) => s + x.count, 0);
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="pointer-events-none absolute left-5 top-[calc(100%-0.5rem)] z-40 w-72 translate-y-1 rounded-lg border bg-popover p-3.5 text-popover-foreground opacity-0 shadow-lg transition-all duration-150 group-hover:translate-y-2 group-hover:opacity-100">
      <span aria-hidden className="absolute -top-1.5 left-8 size-3 rotate-45 border-l border-t bg-popover" />
      <div className="mb-2.5 flex items-center justify-between">
        <span className="eyebrow text-[10px]">Pipeline distribution</span>
        <span className="text-[11px] tabular-nums text-muted-foreground">{total} active</span>
      </div>
      <div className="space-y-1.5">
        {stages.map((s) => (
          <div key={s.key} className="flex items-center gap-2.5 text-xs">
            <span className="w-24 shrink-0 truncate text-muted-foreground">{s.label}</span>
            <div className="h-1.5 flex-1 rounded-full bg-foreground/10">
              <div
                className={cn("h-full rounded-full", s.count > 0 ? "bg-[var(--brand-accent)]" : "")}
                style={{ width: `${(s.count / max) * 100}%` }}
              />
            </div>
            <span
              className={cn(
                "w-4 shrink-0 text-right tabular-nums",
                s.count > 0 ? "text-foreground" : "text-muted-foreground/50",
              )}
            >
              {s.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A slot in the target organisation's reporting tree. `filled` = an existing hire shown for context;
 *  everything else is an OPEN requisition matched (by title) to a live search. This structure is
 *  representative & design-only — the data model has no reporting edges yet (devs wire them later) — so
 *  the chart can read as a real org: who reports to whom, with the open searches highlighted in place. */
type OrgSlot = { title: string; filled?: boolean; reports?: OrgSlot[] };

const ORG: OrgSlot = {
  title: "Chief Executive Officer",
  filled: true,
  reports: [
    {
      title: "Chief Financial Officer",
      reports: [{ title: "VP Finance", reports: [{ title: "Financial Controller" }] }, { title: "Procurement Lead" }],
    },
    {
      title: "Chief Technology Officer",
      reports: [{ title: "VP Engineering", reports: [{ title: "Senior Data Engineer" }] }, { title: "VP Product" }],
    },
    {
      title: "Chief Revenue Officer",
      reports: [{ title: "Director of Marketing" }, { title: "Sales Manager" }],
    },
    {
      title: "Chief People Officer",
      reports: [{ title: "HR Business Partner" }],
    },
    {
      title: "Chief Operating Officer",
      filled: true,
      reports: [{ title: "VP Operations", reports: [{ title: "Operations Analyst" }] }, { title: "Director, Legal" }],
    },
  ],
};

/** The org chart: the client's target organisation as a real reporting tree — each manager's reports
 *  branch directly beneath them (so the shape follows the hierarchy, not the tier). Filled seats give
 *  context; the OPEN requisitions (our live searches) are the highlighted nodes with status + counts.
 *  Connectors are drawn in CSS (`.orgtree`). It's navigable — scroll/buttons to zoom, drag to pan — and
 *  the tier chips + search stay live: they DIM the non-matching cards (never remove them, so branching
 *  stays intact) and a search recentres/zooms the canvas onto the first matching card. */
function RolesOrgChart({ rows, tier, query }: { rows: Row[]; tier: Tier | "all"; query: string }) {
  const byTitle = useMemo(() => new Map(rows.map((r) => [r.title, r])), [rows]);
  const q = query.trim().toLowerCase();
  const filterActive = tier !== "all" || q.length > 0;

  // Is a node relevant under the current filter/search? Tier matches the node's row tier; search matches
  // its title/function/lead. Irrelevant nodes are dimmed rather than removed.
  const isRelevant = useCallback(
    (slot: OrgSlot): boolean => {
      if (!filterActive) return true;
      const row = byTitle.get(slot.title);
      if (tier !== "all" && row?.tier !== tier) return false;
      if (q) {
        const hay = `${slot.title} ${row?.function ?? ""} ${row?.ownerName ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    },
    [filterActive, tier, q, byTitle],
  );

  // The card the search should fly to — first title (DFS) whose text matches the query.
  const focusKey = useMemo(() => {
    if (!q) return null;
    let found: string | null = null;
    const walk = (slot: OrgSlot) => {
      if (found) return;
      const row = byTitle.get(slot.title);
      const hay = `${slot.title} ${row?.function ?? ""} ${row?.ownerName ?? ""}`.toLowerCase();
      if (hay.includes(q)) {
        found = slot.title;
        return;
      }
      slot.reports?.forEach(walk);
    };
    walk(ORG);
    return found;
  }, [q, byTitle]);

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-4 border-b px-5 py-3">
        <div className="flex items-center gap-5 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded border border-foreground/25 bg-card" />
            Open req
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-3 rounded border border-dashed border-border bg-muted/30" />
            Filled
          </span>
        </div>
        <p className="hidden text-[11px] text-muted-foreground sm:block">Scroll to zoom · drag to pan</p>
      </div>
      <OrgCanvas focusKey={focusKey}>
        <div className="orgtree w-max min-w-full px-8 py-10">
          <ul>
            <OrgTreeNode slot={ORG} byTitle={byTitle} isRelevant={isRelevant} filterActive={filterActive} />
          </ul>
        </div>
      </OrgCanvas>
    </div>
  );
}

/** The pan/zoom viewport. A fixed-height window (`overflow-hidden`) over a transformed inner layer:
 *  wheel zooms toward the cursor, dragging empty space pans, the buttons zoom about the centre, and a
 *  `focusKey` change recentres+zooms onto that node. Dragging is skipped when the pointer starts on a
 *  link or control so cards stay clickable. */
const clampScale = (s: number) => Math.min(2, Math.max(0.4, s));

function OrgCanvas({ children, focusKey }: { children: React.ReactNode; focusKey: string | null }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState({ scale: 1, tx: 0, ty: 0 });
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Wheel-zoom toward the cursor. Bound imperatively so we can preventDefault (React's onWheel is passive).
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setT((p) => {
        const next = clampScale(p.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1));
        return { scale: next, tx: cx - ((cx - p.tx) / p.scale) * next, ty: cy - ((cy - p.ty) / p.scale) * next };
      });
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, []);

  // Recentre + gently zoom onto the searched node whenever the focus target changes.
  useEffect(() => {
    if (!focusKey) return;
    const vp = viewportRef.current;
    const node = contentRef.current?.querySelector(`[data-node-key="${CSS.escape(focusKey)}"]`);
    if (!vp || !node) return;
    const vpRect = vp.getBoundingClientRect();
    const nodeRect = (node as HTMLElement).getBoundingClientRect();
    setT((p) => {
      const target = Math.max(p.scale, 1.25);
      const nodeCx = nodeRect.left - vpRect.left + nodeRect.width / 2;
      const nodeCy = nodeRect.top - vpRect.top + nodeRect.height / 2;
      const contentX = (nodeCx - p.tx) / p.scale;
      const contentY = (nodeCy - p.ty) / p.scale;
      return { scale: target, tx: vpRect.width / 2 - contentX * target, ty: vpRect.height / 2 - contentY * target };
    });
  }, [focusKey]);

  const zoomBy = (factor: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const rect = vp.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setT((p) => {
      const next = clampScale(p.scale * factor);
      return { scale: next, tx: cx - ((cx - p.tx) / p.scale) * next, ty: cy - ((cy - p.ty) / p.scale) * next };
    });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("a,button")) return; // keep cards & controls clickable
    drag.current = { x: e.clientX, y: e.clientY, tx: t.tx, ty: t.ty };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setT((p) => ({ ...p, tx: d.tx + (e.clientX - d.x), ty: d.ty + (e.clientY - d.y) }));
  };
  const endDrag = () => {
    drag.current = null;
    setDragging(false);
  };

  return (
    <div className="relative">
      <div
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className={cn("relative h-[460px] touch-none overflow-hidden", dragging ? "cursor-grabbing" : "cursor-grab")}
      >
        <div
          ref={contentRef}
          style={{ transform: `translate(${t.tx}px, ${t.ty}px) scale(${t.scale})`, transformOrigin: "0 0" }}
        >
          {children}
        </div>
      </div>
      <div className="absolute bottom-3 right-3 flex flex-col overflow-hidden rounded-md border bg-card shadow-sm">
        <ZoomBtn label="Zoom in" onClick={() => zoomBy(1.2)}>
          <Plus className="size-4" />
        </ZoomBtn>
        <ZoomBtn label="Zoom out" onClick={() => zoomBy(1 / 1.2)}>
          <Minus className="size-4" />
        </ZoomBtn>
        <ZoomBtn label="Reset view" onClick={() => setT({ scale: 1, tx: 0, ty: 0 })}>
          <Maximize2 className="size-4" />
        </ZoomBtn>
      </div>
    </div>
  );
}

function ZoomBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid size-8 place-items-center text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring [&:not(:last-child)]:border-b"
    >
      {children}
    </button>
  );
}

/** One node in the reporting tree plus its reports (recursively). The `<li>`/`<ul>` nesting is what the
 *  `.orgtree` CSS turns into branch connectors. `dim` fades cards the current filter/search excludes. */
function OrgTreeNode({
  slot,
  byTitle,
  isRelevant,
  filterActive,
}: {
  slot: OrgSlot;
  byTitle: Map<string, Row>;
  isRelevant: (s: OrgSlot) => boolean;
  filterActive: boolean;
}) {
  const row = byTitle.get(slot.title);
  const dim = filterActive && !isRelevant(slot);
  return (
    <li>
      {slot.filled || !row ? <FilledNode title={slot.title} dim={dim} /> : <OpenNode r={row} dim={dim} />}
      {slot.reports && slot.reports.length > 0 ? (
        <ul>
          {slot.reports.map((child) => (
            <OrgTreeNode
              key={child.title}
              slot={child}
              byTitle={byTitle}
              isRelevant={isRelevant}
              filterActive={filterActive}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** An OPEN requisition node — a live search, highlighted with its status and headline counts, and
 *  clickable through to the role. Fixed width so the tree's connectors stay aligned. */
function OpenNode({ r, dim }: { r: Row; dim?: boolean }) {
  const h = health(r);
  return (
    <RoleLink
      to="/search/$searchId"
      params={{ searchId: r.id }}
      data-node-key={r.title}
      className={cn(
        "flex w-40 flex-col items-center gap-1.5 rounded-lg border border-foreground/15 bg-card px-2.5 py-2.5 text-center shadow-sm outline-none transition-[opacity,border-color,background-color] hover:border-foreground/35 hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring",
        dim && "opacity-25",
      )}
    >
      <div className="flex max-w-full items-center gap-1.5">
        <span className="truncate text-[13px] font-medium leading-tight text-foreground">{r.title}</span>
        <OpeningsBadge openings={r.openings} />
      </div>
      <StatusBadge h={h} />
      <span className="text-[10.5px] tabular-nums text-muted-foreground">
        {r.activeCandidates} candidates · {r.nearClosure} near
      </span>
    </RoleLink>
  );
}

/** A FILLED seat — shown only for hierarchy context, so it's deliberately quiet (dashed, muted) and not
 *  interactive; the eye should land on the open reqs around it. */
function FilledNode({ title, dim }: { title: string; dim?: boolean }) {
  return (
    <div
      data-node-key={title}
      className={cn(
        "flex w-44 flex-col items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2.5 text-center transition-opacity",
        dim && "opacity-25",
      )}
    >
      <span className="truncate text-[13px] font-medium leading-tight text-muted-foreground">{title}</span>
      <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
        Filled
      </span>
    </div>
  );
}

function TierChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border-transparent bg-foreground text-background shadow-sm"
          : "border-border bg-card text-muted-foreground hover:border-foreground/30 hover:bg-foreground/[0.04] hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const opts = [
    { v: "table" as const, icon: Rows3, label: "Table view" },
    { v: "org" as const, icon: Network, label: "Org chart view" },
  ];
  return (
    <div className="flex items-center gap-0.5 rounded-lg border bg-card p-0.5">
      {opts.map(({ v, icon: Icon, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-label={label}
          aria-pressed={value === v}
          className={cn(
            "grid size-7 place-items-center rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
            value === v
              ? "bg-foreground text-background shadow-sm"
              : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground",
          )}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </div>
  );
}
